import { TiptapEditorHTMLElement } from '@tiptap/core';
import { CompilerContentMarkup, Loader } from '@ws/shared/components';
import { TNote } from '@ws/shared/types';
import { diff_match_patch } from '@ws/shared/utils';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { EditabilityContext } from '../../context/EditabilityContext';
import { TStatsCounter, updateCounter } from '../../redux/stats';
import { useNoteContainer } from '../TkPanel/noteContainers';

import { LabelWrapper } from './MarkdownEditor';
import { TipTapNoteSplitterExtension } from './NoteSplitterExtension/TipTapNoteSplitterExtension';
import { SaveManagerExtension } from './SaveManagerExtension';
import { TipTapEditor } from './TipTapEditor';

export interface IWriteBoardEditorProps {
  note: TNote;
}

export interface IWriteBoardEditorHandle {
  focusAtEnd: () => void;
}

export const WriteBoardEditor = forwardRef<IWriteBoardEditorHandle, IWriteBoardEditorProps>(
  function WriteBoardEditor({ note }, ref) {
    const noteRef = useRef<TNote>(note);
    const editorRef = useRef<TiptapEditorHTMLElement | null>(null);

    const focusAtEnd = useCallback(() => {
      if (editorRef.current && editorRef.current.editor) {
        const { editor } = editorRef.current;

        // Устанавливаем курсор в конец текста
        editor.chain().focus().setTextSelection(editor.state.doc.content.size).run();
      }
    }, []);

    useImperativeHandle(ref, () => ({ focusAtEnd }), [focusAtEnd]);

    const { updateNoteContent } = useContext(EditabilityContext);
    const noteContainerRef = useNoteContainer(note.id);
    const [noteId, setNoteId] = useState(note.id);
    const [content, setContent] = useState(note.content);
    const [isUpdating, setIsUpdating] = useState(false);

    const dispatch = useDispatch();

    const noteIdRef = useRef<string>(note.id);
    const baseContentRef = useRef<string>(note.content);
    const lastSavedContentRef = useRef<string>(note.content);

    useEffect(() => {
      if (noteIdRef.current !== note.id) {
        // Full reset if it's a different note
        noteIdRef.current = note.id;
        noteRef.current = note;
        baseContentRef.current = note.content;
        lastSavedContentRef.current = note.content;

        setNoteId(note.id);
        setContent(note.content);
        setIsUpdating(false);
      } else {
        // It's the same note. Check for external content updates.

        // 1. If remote is same as base, it means no new changes from server.
        if (note.content === baseContentRef.current) {
          return;
        }

        // 2. ECHO CHECK:
        // If remote matches what we last saved, it means this update is just
        // the server confirming our own save. We accept it as the new base
        // but DO NOT trigger a merge or update local content (because it's already there).
        if (note.content === lastSavedContentRef.current) {
          baseContentRef.current = note.content;
          return;
        }

        // 3. True remote change detected.

        // UX: Show loader to indicate update
        setIsUpdating(true);
        setTimeout(() => setIsUpdating(false), 800);

        // If we haven't changed locally relative to base, we can just take remote.
        if (content === baseContentRef.current) {
          baseContentRef.current = note.content;
          setContent(note.content);
          return;
        }

        // 4. Both changed. We need to merge.
        const dmp = new diff_match_patch();
        const patches = dmp.patch_make(baseContentRef.current, note.content);
        const [newContent] = dmp.patch_apply(patches, content);

        // Update base to match remote, as that is now our new baseline
        baseContentRef.current = note.content;
        setContent(newContent);
      }
    }, [note, content]);

    // contentToSave приходит из SaveManagerExtension (latestContentRef) — рендер-стейт
    // content здесь использовать нельзя: замыкание может быть старше набранного (F19).
    // targetNoteId может указывать на ПРЕДЫДУЩУЮ заметку (flush при переключении) —
    // тогда guard по текущему note.content неприменим, сохраняем адресно как есть.
    function handleSave(contentToSave: string, targetNoteId: string) {
      if (targetNoteId !== noteRef.current.id) {
        updateNoteContent(targetNoteId, contentToSave);
        return;
      }

      if (note.content !== contentToSave) {
        lastSavedContentRef.current = contentToSave; // Track what we are saving
        updateNoteContent(targetNoteId, contentToSave);
      }
    }

    const handleLabelClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (event.target !== event.currentTarget) {
        return;
      }

      focusAtEnd();
    };

    const handleCounterUpdate = (counter: TStatsCounter) => {
      dispatch(updateCounter({ id: noteId, counter }));
    };

    return (
      <>
        <CompilerContentMarkup>
          <SaveManagerExtension
            noteId={noteId}
            value={content}
            onChange={setContent}
            onSave={handleSave}
          >
            {(saverParams) => (
              <TipTapNoteSplitterExtension note={note} onChange={saverParams.onChange}>
                {(noteSplitterParams) => (
                  <LabelWrapper onClick={handleLabelClick}>
                    <div style={{ position: 'relative' }} ref={noteContainerRef}>
                      {isUpdating && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Loader />
                        </div>
                      )}
                      <TipTapEditor
                        ref={editorRef}
                        noteId={saverParams.noteId}
                        note={saverParams.value}
                        onBlur={saverParams.onBlur}
                        onChange={saverParams.onChange}
                        onCounterUpdate={handleCounterUpdate}
                        onCutUpperPart={noteSplitterParams.onSplitNoteAbove}
                        onCutLowerPart={noteSplitterParams.onSplitNoteBelow}
                      />
                    </div>
                  </LabelWrapper>
                )}
              </TipTapNoteSplitterExtension>
            )}
          </SaveManagerExtension>
        </CompilerContentMarkup>
      </>
    );

    // return (
    //   <SaveManagerExtension value={content} onChange={setContent} onSave={handleSave}>
    //     {(saverParams) => (
    //       <SelectionManagerExtension {...saverParams}>
    //         {(selectionParams) => (
    //           <NoteSplitterExtension
    //             note={note}
    //             onChange={saverParams.onChange}
    //             selection={selectionParams.selection}
    //             value={saverParams.value}
    //           >
    //             {(noteSplitterParams) => (
    //               <ContextMenuExtension
    //                 {...noteSplitterParams}
    //                 value={saverParams.value}
    //                 selection={selectionParams.selection}
    //                 onChange={saverParams.onChange}
    //               >
    //                 <LabelWrapper>
    //                   <MarkdownEditor
    //                     value={saverParams.value}
    //                     onChange={saverParams.onChange}
    //                     onBlur={saverParams.onBlur}
    //                     onSelect={selectionParams.onSelect}
    //                   />
    //                 </LabelWrapper>
    //               </ContextMenuExtension>
    //             )}
    //           </NoteSplitterExtension>
    //         )}
    //       </SelectionManagerExtension>
    //     )}
    //   </SaveManagerExtension>
    // );
  },
);

WriteBoardEditor.displayName = 'WriteBoardEditor';
